import { useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, SETUP_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { RowActions } from "@/components/RowActions";
import { ShiftDialog } from "@/components/ShiftDialog";
import { useSchedule } from "@/hooks/useSchedule";
import { usePayrollRoster } from "@/hooks/usePayrollRoster";
import { problemMessage } from "@/api/client";
import type { Shift } from "@/api/types";
import "./Schedule.css";

/**
 * ef-040-3 (D3) — the owner's Schedule screen, `/setup/schedule`: a bounded-window view
 * of `shift` rows (ScheduleEndpoints.cs), grouped by week per ADR 0009's "one page
 * template" — `PageHeader` (title + the one primary action, "New shift") → the Setup
 * area tab strip → content in cards, one card per week.
 *
 * WEEK VIEW, NOT A FIXED 7-DAY GRID. Each card is one calendar week (Monday-start), but
 * the fetch window itself (`useSchedule.ts`) is a wide, bounded rolling horizon rather
 * than "this week alone" — a shift created for next month must appear on THIS SAME
 * screen the instant it saves, with no reload and no "find the right week" navigation
 * step (schedule.spec.ts's own AC2, "each state visible without a reload"). Weeks with no
 * shift in them are simply not rendered — a farmer scrolling this screen is looking for
 * work, not confirming the absence of it forty times over.
 *
 * ONE PRIMARY ACTION PER ROW (ADR 0009 §4) — Edit is the row's own visible button;
 * everything else (Delete) sits behind `RowActions`'s "More actions" disclosure, exactly
 * `TimeApprovalQueue.tsx`'s own precedent. Publishing a shift happens THROUGH Edit (the
 * dialog's own Status field) rather than as a second visible button — schedule.spec.ts's
 * own comment says either implementation is acceptable; this is the one that keeps the
 * row at one button.
 */

function mondayOf(iso: string): Date {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function weekKey(monday: Date): string {
  return monday.toISOString().slice(0, 10);
}

function formatWeekHeading(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  return `Week of ${monday.toLocaleDateString("en-US", opts)} – ${sunday.toLocaleDateString("en-US", opts)}`;
}

function formatShiftWhen(startIso: string, endIso: string): string {
  const day = new Date(startIso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day} · ${time(startIso)}–${time(endIso)}`;
}

/** Groups shifts into Monday-start weeks, each sorted chronologically, weeks themselves
 *  sorted chronologically. Never re-derived elsewhere — this is the one place "which week
 *  does this shift belong to" is decided. */
function groupByWeek(shifts: Shift[]): { key: string; heading: string; shifts: Shift[] }[] {
  const groups = new Map<string, { monday: Date; shifts: Shift[] }>();
  for (const shift of shifts) {
    const monday = mondayOf(shift.scheduledStart);
    const key = weekKey(monday);
    const existing = groups.get(key);
    if (existing) {
      existing.shifts.push(shift);
    } else {
      groups.set(key, { monday, shifts: [shift] });
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, { monday, shifts: weekShifts }]) => ({
      key,
      heading: formatWeekHeading(monday),
      shifts: weekShifts.sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart)),
    }));
}

export function Schedule() {
  const { shifts, status, error, create, update, remove } = useSchedule();
  const { employees } = usePayrollRoster("primary");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Shift | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const weeks = useMemo(() => groupByWeek(shifts ?? []), [shifts]);

  function openCreate(): void {
    setEditing(null);
    setActionError(null);
    setDialogOpen(true);
  }

  function openEdit(shift: Shift): void {
    setEditing(shift);
    setActionError(null);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    setEditing(null);
  }

  async function deleteShift(id: string): Promise<void> {
    setActionError(null);
    try {
      await remove(id);
    } catch (err) {
      setActionError(problemMessage(err, "Could not delete this shift."));
    }
  }

  return (
    <main className="schedule-page">
      <PageHeader
        title="Schedule"
        subtitle="Plan and publish your team's shifts."
        action={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New shift
          </button>
        }
      />

      <AreaTabs area="/setup" tabs={SETUP_TABS} label="Setup" />

      <section
        className="schedule-page__card"
        aria-labelledby="schedule-heading"
        data-state={status === "ready" && weeks.length === 0 ? "empty" : status}
      >
        <h2 id="schedule-heading" className="schedule-page__card-title">
          Upcoming shifts
        </h2>

        {status === "loading" && <StateMessage tone="loading" title="Loading the schedule…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load the schedule." detail={error ?? undefined} />
        )}

        {status === "ready" && weeks.length === 0 && (
          <StateMessage
            tone="empty"
            title="Nothing scheduled yet."
            detail="Add a shift and it will show up here, grouped by week."
          />
        )}

        {actionError && (
          <p className="schedule-page__error" role="alert">
            {actionError}
          </p>
        )}

        {status === "ready" && weeks.length > 0 && (
          <div className="schedule-page__weeks">
            {weeks.map((week) => (
              <div key={week.key} className="schedule-page__week">
                <h3 className="schedule-page__week-heading">{week.heading}</h3>
                <ul className="schedule-page__list" aria-label={week.heading}>
                  {week.shifts.map((shift) => (
                    <ShiftRow key={shift.id} shift={shift} onEdit={openEdit} onDelete={deleteShift} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {dialogOpen && (
        <ShiftDialog
          employees={(employees ?? []).filter((e) => e.active !== false)}
          shift={editing}
          onClose={closeDialog}
          onCreate={create}
          onUpdate={update}
        />
      )}
    </main>
  );
}

function ShiftRow({
  shift,
  onEdit,
  onDelete,
}: {
  shift: Shift;
  onEdit: (shift: Shift) => void;
  onDelete: (id: string) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function confirmDelete(): Promise<void> {
    setDeleting(true);
    await onDelete(shift.id);
    setDeleting(false);
  }

  return (
    <li className="schedule-page__row" data-shift-id={shift.id} data-state={shift.status}>
      <span className="schedule-page__meta">
        <span className="schedule-page__name">{shift.employeeName}</span>
        <span className="schedule-page__when">{formatShiftWhen(shift.scheduledStart, shift.scheduledEnd)}</span>
        {shift.place && <span className="schedule-page__place">{shift.place}</span>}
      </span>

      <span className="schedule-page__status">{shift.status}</span>

      <span className="schedule-page__actions">
        <button type="button" className="btn btn--primary schedule-page__btn" onClick={() => onEdit(shift)}>
          Edit
        </button>

        <RowActions onOpenChange={(open) => !open && setConfirming(false)}>
          {confirming ? (
            <span className="schedule-page__confirm">
              <span className="schedule-page__confirm-copy">Delete this shift? This cannot be undone.</span>
              <button
                type="button"
                className="btn btn--danger schedule-page__btn"
                disabled={deleting}
                onClick={() => void confirmDelete()}
              >
                {deleting ? "…" : "Delete"}
              </button>
              <button
                type="button"
                className="btn btn--ghost schedule-page__btn"
                disabled={deleting}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn--ghost schedule-page__btn"
              onClick={() => setConfirming(true)}
            >
              Delete
            </button>
          )}
        </RowActions>
      </span>
    </li>
  );
}
